import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'

// ---------------------------------------------------------------------------
// MarkerRepulsion
// ---------------------------------------------------------------------------
// Runs at R3F priority 1 (after drei's Html useFrame at priority 0).
//
// ARCHITECTURE: drei exclusively owns el.style.transform on the portal wrapper
// div. We never touch it. Instead we write CSS `translate` to the inner
// .textButtonContainer div — a separate property on a separate element that
// drei never touches. The two systems never conflict.
//
// Each frame:
//   1. Query all .sec-html-portal / .poi-html-portal wrapper divs.
//   2. Read getBoundingClientRect() on the inner .textButtonContainer,
//      then subtract the current smoothed offset so overlap tests work from
//      the natural drei-projected position (no feedback-loop oscillation).
//   3. Single pass of pairwise AABB repulsion (one clean pass is more stable
//      than multiple passes on a static rect — multi-pass accumulates force
//      without updating rects mid-iteration, causing 4× overshoot).
//   4. Lerp each offset toward the target with a frame-rate-independent alpha.
//   5. Write `contentDiv.style.translate` only when the value has changed.
// ---------------------------------------------------------------------------

const LERP_SPEED = 0.18  // blend factor at 60fps — slightly snappier than before

// Per-breakpoint tuning: [padding, maxOffset, xBias]
//   padding   — px gap enforced between touching bounding boxes
//   maxOffset — px hard cap on offset magnitude
//   xBias     — multiplier on overlapX before axis comparison:
//               lower = more horizontal push preference
const BREAKPOINT = {
    mobileLandscape: { padding: 6,  maxOffset: 120, xBias: 0.35 },
    mobilePortrait:  { padding: 10, maxOffset: 160, xBias: 0.7  },
    default:         { padding: 12, maxOffset: 200, xBias: 1.0  },
}

const MOBILE_PORTRAIT_MQL  = window.matchMedia('(max-width: 767px) and (orientation: portrait)')
const MOBILE_LANDSCAPE_MQL = window.matchMedia('(max-width: 899px) and (orientation: landscape)')

export function MarkerRepulsion() {
    // Keyed by .textButtonContainer element → { x, y } smoothed offset
    const offsetsMap = useRef(new Map())

    useFrame((_, delta) => {
        // Clamp delta so a tab coming back from sleep doesn't cause a huge jump
        const dt = Math.min(delta, 0.1)

        const bp = MOBILE_LANDSCAPE_MQL.matches ? BREAKPOINT.mobileLandscape
                 : MOBILE_PORTRAIT_MQL.matches  ? BREAKPOINT.mobilePortrait
                 : BREAKPOINT.default

        const { padding, maxOffset, xBias } = bp

        // Gather all visible portal wrapper divs across SEC and standard POI markers.
        // NOTE: poi-html-portal-sec006 is intentionally excluded — SEC006 markers
        // are managed by POI_Markers_SEC006 and must not be repelled.
        const portals = document.querySelectorAll('.sec-html-portal, .poi-html-portal')
        if (!portals.length) return

        const offsets = offsetsMap.current
        const markers = []

        portals.forEach((portal) => {
            // Skip portals drei has hidden (marker behind camera)
            if (portal.style.display === 'none') return

            const div = portal.querySelector('.textButtonContainer')
            if (!div) return

            // Skip logically hidden markers (opacity:0 placeholders)
            if (div.classList.contains('sec-hidden')) return

            // Ensure this div has an offset entry
            if (!offsets.has(div)) offsets.set(div, { x: 0, y: 0 })

            const raw = div.getBoundingClientRect()
            if (raw.width === 0 || raw.height === 0) return

            // Subtract current smoothed offset so the overlap test always works
            // from the natural drei-projected position — prevents feedback oscillation
            const off = offsets.get(div)
            markers.push({
                div,
                rect: {
                    left:   raw.left   - off.x,
                    top:    raw.top    - off.y,
                    width:  raw.width,
                    height: raw.height,
                },
            })
        })

        // Prune offsets for elements no longer in the DOM
        for (const key of offsets.keys()) {
            if (!document.contains(key)) offsets.delete(key)
        }

        if (markers.length < 2) {
            // Nothing to repel — decay any lingering offsets to zero
            offsets.forEach((off, div) => {
                if (off.x === 0 && off.y === 0) return
                off.x *= 0.8
                off.y *= 0.8
                if (Math.abs(off.x) < 0.05) off.x = 0
                if (Math.abs(off.y) < 0.05) off.y = 0
                div.style.translate = off.x === 0 && off.y === 0 ? '' : `${off.x}px ${off.y}px`
            })
            return
        }

        // ----------------------------------------------------------------
        // Pairwise AABB repulsion — single pass
        //
        // A single pass with correct 0.5× split is strictly convergent.
        // Multiple passes on a static rect re-accumulate force for the same
        // overlap, causing N× overshoot and oscillation.
        // ----------------------------------------------------------------
        const forces = markers.map(() => ({ x: 0, y: 0 }))

        for (let i = 0; i < markers.length; i++) {
            for (let j = i + 1; j < markers.length; j++) {
                const ri = markers[i].rect
                const rj = markers[j].rect

                const ciX = ri.left + ri.width  * 0.5
                const ciY = ri.top  + ri.height * 0.5
                const cjX = rj.left + rj.width  * 0.5
                const cjY = rj.top  + rj.height * 0.5

                const halfW = (ri.width  + rj.width)  * 0.5 + padding
                const halfH = (ri.height + rj.height) * 0.5 + padding

                const dx = ciX - cjX
                const dy = ciY - cjY

                const overlapX = halfW - Math.abs(dx)
                const overlapY = halfH - Math.abs(dy)

                if (overlapX > 0 && overlapY > 0) {
                    let pushX = 0
                    let pushY = 0
                    if (overlapX * xBias < overlapY) {
                        pushX = overlapX * 0.5 * Math.sign(dx || 1)
                    } else {
                        pushY = overlapY * 0.5 * Math.sign(dy || 1)
                    }
                    forces[i].x += pushX
                    forces[i].y += pushY
                    forces[j].x -= pushX
                    forces[j].y -= pushY
                }
            }
        }

        // ----------------------------------------------------------------
        // Lerp offsets toward forces; write CSS translate to content div
        // ----------------------------------------------------------------
        const alpha = 1 - Math.pow(1 - LERP_SPEED, dt * 60)

        markers.forEach(({ div }, i) => {
            const off = offsets.get(div)
            if (!off) return

            const targetX = Math.max(-maxOffset, Math.min(maxOffset, forces[i].x))
            const targetY = Math.max(-maxOffset, Math.min(maxOffset, forces[i].y))

            off.x += (targetX - off.x) * alpha
            off.y += (targetY - off.y) * alpha

            if (Math.abs(off.x) < 0.05) off.x = 0
            if (Math.abs(off.y) < 0.05) off.y = 0

            // Skip write if nothing changed
            if (off.x === 0 && off.y === 0 && targetX === 0 && targetY === 0) {
                if (div.style.translate) div.style.translate = ''
                return
            }

            div.style.translate = `${off.x}px ${off.y}px`
        })
    }, 1)

    return null
}
